import {
  useAppointmentsContext,
  useDeliveries,
  useDeliveryCounts,
  useDelivery,
} from '@fidalia/api';
import { Alert } from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { toEnvoi } from '@/features/file-attente/api';
import {
  NO_FILTERS,
  VIEW_BY_TAB,
  type FiltreBoite,
  type AdvancedFilters,
} from '@/features/file-attente/boite';
import { BoiteMail } from '@/features/file-attente/boite-mail';

export const Route = createFileRoute('/_app/file-attente')({
  component: FileAttentePage,
});

/** Laisse finir la frappe avant d'interroger le serveur. */
function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Au-delà de ce délai sans lecture, les rappels des rendez-vous récents peuvent manquer. */
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/** « il y a 2 jours », « il y a 5 heures ». */
function elapsedLabel(since: Date, now: number): string {
  const hours = Math.floor((now - since.getTime()) / 3_600_000);
  return hours < 48 ? `il y a ${hours} heures` : `il y a ${Math.floor(hours / 24)} jours`;
}

/**
 * L'extension lit les rendez-vous tant qu'un onglet Doctolib est ouvert. Si le
 * cabinet ne l'ouvre plus, rien ne se programme et rien ne le dit : cet
 * avertissement est le seul moyen de s'en apercevoir avant qu'un patient
 * n'ait pas reçu son rappel.
 */
function SyncWarning() {
  const { data } = useAppointmentsContext();

  // L'heure vit dans l'état : lue pendant le rendu, elle ne serait pas pure, et
  // une page laissée ouverte des jours ne s'apercevrait jamais que la lecture
  // est devenue périmée.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (!data) return null;

  if (data.lastSyncAt === null) {
    return (
      <Alert tone="info" title="Aucun rendez-vous synchronisé">
        Ouvrez Doctolib avec l’extension Fidalia pour que vos rendez-vous soient lus et vos messages
        programmés.
      </Alert>
    );
  }

  const last = new Date(data.lastSyncAt);
  if (now - last.getTime() < STALE_AFTER_MS) return null;

  return (
    <Alert tone="warning" title={`Dernière synchronisation ${elapsedLabel(last, now)}`}>
      Les rendez-vous pris depuis ne sont pas encore programmés. Ouvrez Doctolib avec un navigateur
      ayant l’extension Fidalia installée afin de lire les derniers rendez-vous.
    </Alert>
  );
}

/**
 * Ce que Fidalia s'apprête à envoyer, en boîte de réception.
 *
 * Une grille de semaine a été essayée ici, cinq colonnes de jours, feuilletées,
 * et écartée : elle disait bien *quand* les messages partent, mais jamais
 * *quoi*, et c'est le texte que le cabinet vient vérifier avant d'arrêter un
 * envoi. Montrer un courriel y demandait d'ouvrir une boîte de dialogue à
 * chaque ligne, là où deux panneaux le donnent d'un clic.
 *
 * Pas de `PageHeader` : la boîte occupe l'écran entier et porte son propre
 * titre, comme toute messagerie. La hauteur fait la value de cet écran, une
 * liste de dix lignes plutôt que de six.
 *
 * Les onglets du haut découpent la liste, ils ne changent pas d'écran : c'est
 * ce qui distingue une boîte de réception de trois pages.
 *
 * Les lignes, leur tri, la recherche et les filtres viennent du serveur : la
 * liste est paginée, et filtrer dans le navigateur ne verrait que la page
 * chargée.
 */
function FileAttentePage() {
  // Ce qui reste à partir en premier : c'est la seule vue où quelque chose se
  // décide encore.
  const [filtre, setFiltre] = useState<FiltreBoite>('a-envoyer');
  const [recherche, setRecherche] = useState('');
  const [advanced, setAvances] = useState<AdvancedFilters>(NO_FILTERS);

  /**
   * L'identifiant du message ouvert, pas le message lui-même.
   *
   * Garder l'objet aurait figé une copie : après une annulation ou un
   * changement de filtre, le panneau de droite aurait continué d'afficher une
   * ligne qui n'est plus dans la liste.
   */
  const [ouvertId, setOuvertId] = useState<string | null>(null);

  const debouncedTerm = useDebounced(recherche.trim());

  const liste = useDeliveries({
    view: VIEW_BY_TAB[filtre],
    q: debouncedTerm || undefined,
    from: advanced.from || undefined,
    to: advanced.to || undefined,
    types: advanced.types,
    agendas: advanced.agendas,
  });
  const counts = useDeliveryCounts();

  const envois = useMemo(
    () => liste.data?.pages.flatMap((page) => page.items.map(toEnvoi)) ?? [],
    [liste.data],
  );

  /**
   * Rien d'ouvert tant que rien n'a été choisi.
   *
   * Ouvrir d'office le premier de la liste aurait désigné une ligne
   * quelconque comme celle qui compte, et, le jour où l'état « lu » existera,
   * l'aurait marquée lue sans que personne l'ait regardée.
   *
   * La recherche par identifiant vaut aussi filet : un message annulé, ou
   * sorti de la vue après un changement d'onglet, referme le panneau au lieu
   * d'y rester affiché.
   */
  const ouvert = envois.find((envoi) => envoi.id === ouvertId) ?? null;
  const detail = useDelivery(ouvert?.id ?? null);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <SyncWarning />
      <BoiteMail
        envois={envois}
        loading={liste.isPending}
        hasError={liste.isError}
        onRetry={() => void liste.refetch()}
        hasMore={liste.hasNextPage}
        loadingMore={liste.isFetchingNextPage}
        onLoadMore={() => void liste.fetchNextPage()}
        counts={counts.data}
        filtre={filtre}
        onFiltre={(value) => {
          setFiltre(value);
          setOuvertId(null);
        }}
        recherche={recherche}
        onRecherche={setRecherche}
        advanced={advanced}
        onAdvanced={setAvances}
        ouvert={ouvert}
        detail={detail.data}
        detailLoading={detail.isPending && ouvert !== null}
        onOuvrir={(envoi) => setOuvertId(envoi.id)}
        onFermer={() => setOuvertId(null)}
        refreshing={liste.isFetching || counts.isFetching || detail.isFetching}
        onRefresh={() => {
          void liste.refetch();
          void counts.refetch();
          if (ouvert) void detail.refetch();
        }}
      />
    </div>
  );
}
